import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import {
  Check,
  ChefHat,
  CircleEllipsis,
  Clock,
  LoaderCircle,
  PackageX,
  Play,
  RotateCcw,
  ShieldAlert,
  TriangleAlert,
  Wrench,
  type LucideIcon,
} from 'lucide-react'
import { decodeRoles, initializeSession } from '../../lib/auth'
import type { Lang } from '../../lib/i18n'
import {
  KITCHEN_BLOCK_REASONS,
  blockTask,
  canActInKitchen,
  canResumeInKitchen,
  canSeeKitchen,
  completeTask,
  fetchKitchenTasks,
  resumeTask,
  startTask,
  type KitchenBlockReason,
  type KitchenOpenStatus,
  type KitchenTask,
} from '../../lib/kitchen-api'
import { CommandHeader } from '../../components/CommandHeader'
import { HttpError } from '../../lib/orders-api'
import { Bilingual, fromPair } from '../../components/Bilingual'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog'
import { KITCHEN_LABEL, type KitchenLabelKey } from './kitchen-i18n'
import { kitchenOrderCode } from './kitchen-code'

// R199 (b) — the kitchen's task list inside HOP. Online only: a write made offline fails with
// a visible error, never a queue (the offline queue would turn a 409 into a terminal failure).

interface Props {
  lang: Lang
  onLangToggle: () => void
  isOnline: boolean
}

// Same cadence as the Board's silent poll.
const POLL_INTERVAL_MS = 20_000
const NOTE_MAX = 500

// D23 status colours (PENDING amber, IN_PROGRESS navy, BLOCKED red) on the artboard's tokens,
// each with an icon so the chip reads without words. className on Badge only.
const STATUS_CLASS: Record<KitchenOpenStatus, string> = {
  PENDING: 'border-transparent bg-amber-tint text-amber-ink',
  IN_PROGRESS: 'border-transparent bg-navy-tint text-navy',
  BLOCKED: 'border-transparent bg-destructive-tint text-destructive',
}

const STATUS_ICON: Record<KitchenOpenStatus, LucideIcon> = {
  PENDING: Clock,
  IN_PROGRESS: LoaderCircle,
  BLOCKED: TriangleAlert,
}

const REASON_ICON: Record<KitchenBlockReason, LucideIcon> = {
  MISSING_INGREDIENT: PackageX,
  ALLERGY_SAFETY: ShieldAlert,
  EQUIPMENT: Wrench,
  OTHER: CircleEllipsis,
}

type WriteKey = 'start' | 'block' | 'resume' | 'ready'

// `modifications` is a JSON column: a string, an array, an object or null. The kitchen sees the
// values only (UX form pass bench 4): an object's keys are JSON field names, never shown, and a
// nested value is walked for its values. The raw value is never a React child.
function modificationValues(value: unknown): string[] {
  if (value === null || value === undefined) return []
  if (typeof value === 'string') return value.trim() === '' ? [] : [value.trim()]
  if (typeof value === 'number' || typeof value === 'boolean') return [String(value)]
  if (Array.isArray(value)) return value.flatMap(modificationValues)
  if (typeof value === 'object') return Object.values(value as Record<string, unknown>).flatMap(modificationValues)
  return []
}

function renderModifications(value: unknown): ReactNode {
  const values = modificationValues(value)
  if (values.length === 0) return null
  return (
    <ul data-kitchen="changes" className="flex flex-wrap gap-2">
      {values.map((v, i) => (
        <li key={i} className="rounded-full bg-amber-tint px-3 py-1 text-base font-medium text-amber-ink">
          {v}
        </li>
      ))}
    </ul>
  )
}

// The 96 px photo slot: the product photo when the kitchen read carries one, else a chef-hat
// placeholder. A photo that fails to load falls back to the placeholder (failure has a face).
function TaskPhoto({ url }: { url: string | null }) {
  const [failed, setFailed] = useState(false)
  useEffect(() => setFailed(false), [url])
  return (
    <div
      data-kitchen="photo"
      className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-sage-tint"
    >
      {url && !failed ? (
        <img src={url} alt="" className="h-full w-full object-cover" onError={() => setFailed(true)} />
      ) : (
        <ChefHat aria-hidden data-kitchen="photo-placeholder" className="h-10 w-10 text-sage-ink" />
      )}
    </div>
  )
}

// The pending face on the button being written: a spinner in place of its icon.
function ActionIcon({ Icon, active }: { Icon: LucideIcon; active: boolean }) {
  return active ? <LoaderCircle aria-hidden className="animate-spin" /> : <Icon aria-hidden />
}

// Full-width icon buttons, sized per the artboard: Complete largest, then Start and Resume, then Block.
const ACTION_BASE = 'w-full gap-3 rounded-xl text-xl font-semibold [&_svg]:size-6'

function reasonLabel(reason: string) {
  const key = `reason${reason}` as KitchenLabelKey
  return KITCHEN_LABEL[key] ?? { en: reason, ar: null }
}

interface CardProps {
  task: KitchenTask
  canAct: boolean
  canResume: boolean
  pending: WriteKey | null
  error: string | null
  onWrite: (task: KitchenTask, key: WriteKey, fn: () => Promise<void>) => Promise<void>
}

// The block dialog's open state, reason and note live here, keyed by taskId in the list: a
// poll replaces the list's data but never this card's state.
function TaskCard({ task, canAct, canResume, pending, error, onWrite }: CardProps) {
  const [blockOpen, setBlockOpen] = useState(false)
  const [reason, setReason] = useState<KitchenBlockReason | ''>('')
  const [note, setNote] = useState('')

  const open = task.status === 'PENDING' || task.status === 'IN_PROGRESS'
  const date = typeof task.deliveryDate === 'string' ? task.deliveryDate.slice(0, 10) : null
  const modifications = renderModifications(task.modifications)
  const busy = pending !== null
  // /kitchen reads without a window, so COMPLETED never reaches this card (R285 widened the type).
  const shownStatus = task.status as KitchenOpenStatus
  const StatusIcon = STATUS_ICON[shownStatus]
  const code = kitchenOrderCode(task.orderId, task.orderCode)

  async function submitBlock() {
    if (reason === '') return
    const trimmed = note.trim()
    await onWrite(task, 'block', () => blockTask(task.taskId, reason, trimmed === '' ? undefined : trimmed))
    setBlockOpen(false)
    setReason('')
    setNote('')
  }

  return (
    <article
      data-task={task.taskId}
      className={`flex flex-col gap-4 rounded-2xl bg-card p-4 shadow-sm ${
        task.status === 'IN_PROGRESS' ? 'border-2 border-navy' : 'border'
      }`}
    >
      <div className="flex items-start gap-4">
        <TaskPhoto url={task.imageUrl} />
        <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
          <div data-kitchen="name" className="text-[26px] font-bold leading-tight">
            <Bilingual ar={task.nameAr} en={task.nameEn} />
          </div>
          <div className="text-base text-muted-foreground">
            <span dir="ltr" data-kitchen="code">
              {code}
            </span>
            {task.firstName ? <span>{` \u00b7 ${task.firstName}`}</span> : null}
          </div>
          {date || task.deliverySlot ? (
            <span dir="ltr" className="text-sm text-muted-foreground">
              {[date, task.deliverySlot].filter(Boolean).join(' ')}
            </span>
          ) : null}
          <Badge
            variant="outline"
            data-kitchen="status"
            className={`gap-1.5 rounded-full px-3 py-1 text-sm ${STATUS_CLASS[shownStatus]}`}
          >
            <StatusIcon aria-hidden data-kitchen="status-icon" className="h-4 w-4 shrink-0" />
            {fromPair(KITCHEN_LABEL[`status${shownStatus}`], undefined, 'inherit')}
          </Badge>
        </div>
        <span
          dir="ltr"
          data-kitchen="quantity"
          className="shrink-0 rounded-xl bg-primary px-4 py-1 text-4xl font-bold text-primary-foreground"
        >
          {`\u00d7${task.quantity}`}
        </span>
      </div>

      {modifications}
      {task.orderNotes ? <p className="text-sm text-muted-foreground">{task.orderNotes}</p> : null}

      {task.status === 'BLOCKED' ? (
        <div data-kitchen="reason" className="rounded-md bg-destructive-tint p-2 text-sm text-destructive">
          {task.blockReason ? <div>{fromPair(reasonLabel(task.blockReason), undefined, 'inherit')}</div> : null}
          {task.blockNote ? <div>{task.blockNote}</div> : null}
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        {canAct && task.status === 'PENDING' ? (
          <Button
            data-action="start"
            disabled={busy}
            aria-busy={pending === 'start'}
            className={`${ACTION_BASE} h-16`}
            onClick={() => onWrite(task, 'start', () => startTask(task.taskId))}
          >
            <ActionIcon Icon={Play} active={pending === 'start'} />
            {fromPair(KITCHEN_LABEL.actionStart, undefined, 'inherit')}
          </Button>
        ) : null}
        {canAct && open ? (
          <Button
            data-action="ready"
            disabled={busy}
            aria-busy={pending === 'ready'}
            className={`${ACTION_BASE} h-[72px] bg-sage-ink text-primary-foreground hover:bg-sage-ink/85`}
            onClick={() => onWrite(task, 'ready', () => completeTask(task.taskId))}
          >
            <ActionIcon Icon={Check} active={pending === 'ready'} />
            {fromPair(KITCHEN_LABEL.actionReady, undefined, 'inherit')}
          </Button>
        ) : null}
        {canAct && open ? (
          <Button
            data-action="block"
            variant="outline"
            disabled={busy}
            className={`${ACTION_BASE} h-14 border-2 border-destructive bg-card text-destructive hover:bg-destructive-tint hover:text-destructive`}
            onClick={() => setBlockOpen(true)}
          >
            <TriangleAlert aria-hidden />
            {fromPair(KITCHEN_LABEL.actionBlock, undefined, 'inherit')}
          </Button>
        ) : null}
        {canResume && task.status === 'BLOCKED' ? (
          <Button
            data-action="resume"
            variant="outline"
            disabled={busy}
            aria-busy={pending === 'resume'}
            className={`${ACTION_BASE} h-16 border-2 border-navy bg-card text-navy hover:bg-navy-tint hover:text-navy`}
            onClick={() => onWrite(task, 'resume', () => resumeTask(task.taskId))}
          >
            <ActionIcon Icon={RotateCcw} active={pending === 'resume'} />
            {fromPair(KITCHEN_LABEL.actionResume, undefined, 'inherit')}
          </Button>
        ) : null}
      </div>

      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}

      <Dialog open={blockOpen} onOpenChange={setBlockOpen}>
        <DialogContent data-dialog-task={task.taskId}>
          <DialogHeader>
            <DialogTitle>{fromPair(KITCHEN_LABEL.blockTitle)}</DialogTitle>
          </DialogHeader>
          <fieldset className="flex flex-col gap-2">
            <legend className="text-sm font-medium">{fromPair(KITCHEN_LABEL.reasonLabel)}</legend>
            <div className="grid grid-cols-2 gap-3">
              {KITCHEN_BLOCK_REASONS.map((r) => {
                const Icon = REASON_ICON[r]
                return (
                  <label key={r} data-kitchen="reason-tile" className="block cursor-pointer">
                    <input
                      type="radio"
                      name={`block-reason-${task.taskId}`}
                      value={r}
                      checked={reason === r}
                      onChange={() => setReason(r)}
                      className="peer sr-only"
                    />
                    <span className="flex min-h-[96px] flex-col items-center justify-center gap-2 rounded-xl border-2 border-border bg-card p-3 text-center text-base font-medium hover:bg-secondary peer-checked:border-destructive peer-checked:bg-destructive-tint peer-checked:text-destructive peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2">
                      <Icon aria-hidden className="h-8 w-8" />
                      {fromPair(reasonLabel(r), undefined, 'inherit')}
                    </span>
                  </label>
                )
              })}
            </div>
          </fieldset>
          <label className="flex flex-col gap-1 text-sm font-medium">
            {fromPair(KITCHEN_LABEL.noteLabel)}
            <textarea
              data-field="note"
              maxLength={NOTE_MAX}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="rounded-md border px-3 py-2 text-sm font-normal"
            />
          </label>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="min-h-[44px]" onClick={() => setBlockOpen(false)}>
              {fromPair(KITCHEN_LABEL.cancel, undefined, 'inherit')}
            </Button>
            <Button data-action="submit-block" disabled={reason === '' || busy} className="min-h-[44px]" onClick={submitBlock}>
              {fromPair(KITCHEN_LABEL.submitBlock, undefined, 'inherit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </article>
  )
}

export function KitchenScreen({ lang, onLangToggle, isOnline }: Props) {
  const [authState, setAuthState] = useState<'checking' | 'ok' | 'redirect'>('checking')
  const [roles, setRoles] = useState<string[]>([])
  const [tasks, setTasks] = useState<KitchenTask[]>([])
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [pending, setPending] = useState<Record<string, WriteKey>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [changedCode, setChangedCode] = useState<string | null>(null)
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    initializeSession().then(() => {
      if (!alive.current) return
      const r = decodeRoles()
      setRoles(r)
      setAuthState(canSeeKitchen(r) ? 'ok' : 'redirect')
    })
    return () => {
      alive.current = false
    }
  }, [])

  const load = useCallback(async (silent: boolean) => {
    if (!silent) setLoadState('loading')
    try {
      const list = await fetchKitchenTasks()
      if (!alive.current) return
      setTasks(list)
      setLoadState('ready')
    } catch {
      // A silent poll that fails keeps what is on screen; only a manual load shows the face.
      if (!silent && alive.current) setLoadState('error')
    }
  }, [])

  useEffect(() => {
    if (authState !== 'ok') return
    void load(false)
    const id = window.setInterval(() => void load(true), POLL_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [authState, load])

  const handleWrite = useCallback(
    async (task: KitchenTask, key: WriteKey, fn: () => Promise<void>) => {
      setChangedCode(null)
      setErrors((e) => {
        const { [task.taskId]: _drop, ...rest } = e
        return rest
      })
      setPending((p) => ({ ...p, [task.taskId]: key }))
      try {
        await fn()
        await load(true)
      } catch (err) {
        if (err instanceof HttpError && err.status === 409) {
          // A 409 is a face: show the task's real state, never a silent no-op. The line is
          // the screen's, so it outlives a task that is no longer on the list.
          setChangedCode(kitchenOrderCode(task.orderId, task.orderCode))
          await load(true)
        } else if (alive.current) {
          const message = err instanceof Error ? err.message : String(err)
          setErrors((e) => ({ ...e, [task.taskId]: message }))
        }
      } finally {
        if (alive.current) {
          setPending((p) => {
            const { [task.taskId]: _drop, ...rest } = p
            return rest
          })
        }
      }
    },
    [load],
  )

  if (authState === 'checking') return null
  if (authState === 'redirect') return <Navigate to="/login" replace />

  const dir = lang === 'ar' ? 'rtl' : 'ltr'
  const canAct = canActInKitchen(roles)
  const canResume = canResumeInKitchen(roles)

  return (
    <div dir={dir} className="min-h-screen bg-background font-sans">
      <CommandHeader
        title={(lang === 'ar' ? KITCHEN_LABEL.title.ar : null) ?? KITCHEN_LABEL.title.en}
        lang={lang}
        onLangToggle={onLangToggle}
        isOnline={isOnline}
      />

      <main className="mx-auto max-w-5xl px-4 py-6 pb-10">
        {changedCode !== null ? (
          <p role="status" className="mb-4 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
            <span className="font-semibold">{changedCode}</span> {fromPair(KITCHEN_LABEL.changed)}
          </p>
        ) : null}

        {loadState === 'loading' && (
          <div className="flex min-h-[40vh] items-center justify-center text-muted-foreground">
            {fromPair(KITCHEN_LABEL.loading)}
          </div>
        )}

        {loadState === 'error' && (
          <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-center">
            <p className="text-muted-foreground">{fromPair(KITCHEN_LABEL.loadError)}</p>
            <Button size="lg" className="min-h-[48px]" onClick={() => void load(false)}>
              {fromPair(KITCHEN_LABEL.retry, undefined, 'inherit')}
            </Button>
          </div>
        )}

        {loadState === 'ready' && tasks.length === 0 && (
          <div className="flex min-h-[40vh] items-center justify-center text-muted-foreground">
            {fromPair(KITCHEN_LABEL.empty)}
          </div>
        )}

        {loadState === 'ready' && tasks.length > 0 && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {tasks.map((task) => (
              <TaskCard
                key={task.taskId}
                task={task}
                canAct={canAct}
                canResume={canResume}
                pending={pending[task.taskId] ?? null}
                error={errors[task.taskId] ?? null}
                onWrite={handleWrite}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
